import { GOOGLE_MAP_KEY } from '@env';
import { Point } from 'geojson';
import jsPDF from 'jspdf';
import capitalize from 'lodash/capitalize';

import { Injectable } from '@angular/core';

import { SnCurrentOrganization, SnCurrentUser } from '@lib/auth';
import { FsFormatColorPipe, FsStateConvertPipe, FsFormatDatePipe } from '@lib/common/pipes';

import { IBMPlexSansFont } from '../../../../ui/src/assets/fonts/IBMPlexSans/IBMPlexSans-Regular';
import { FsLprFleetRead } from '../api/models';

@Injectable({
  providedIn: 'root',
})
export class FsLprReportGeneratorService {
  private _stateConvertPipe = new FsStateConvertPipe();
  private _formatColorPipe = new FsFormatColorPipe();
  private _formatDatePipe = new FsFormatDatePipe();

  constructor(
    private readonly _currentUser: SnCurrentUser,
    private readonly _currentOrganization: SnCurrentOrganization,
  ) {}

  public generatePdfReport(data: FsLprFleetRead): Promise<Blob> {
    return new Promise((resolve) => {
      const doc = new jsPDF();
      doc.addFileToVFS('IBMPlexSans-Regular.ttf', IBMPlexSansFont);
      doc.addFont('IBMPlexSans-Regular.ttf', 'IBMPlexSans', 'normal');
      doc.setFont('IBMPlexSans', 'normal');

      // Header Section with Logo
      this.getLogoBase64('/assets/black-v2.png', (logoBase64) => {
        if (logoBase64) {
          const logoWidth = 50; // Define a proper width
          const logoHeight = 6; // Maintain aspect ratio
          doc.addImage(logoBase64, 'PNG', 125, 15, logoWidth, logoHeight);
        }
        doc.setFontSize(12);
        doc.text(`Department: ${this._currentOrganization.name}`, 10, 10);
        doc.text(
          `Document Generated: ${this._formatDatePipe.transform(new Date(), 'timeFirstNoAmPm')}`,
          10,
          20,
        );
        doc.text(
          `Generated by: ${this._currentUser.fullName} (${this._currentUser.email})`,
          10,
          30,
        );
        doc.line(10, 35, 200, 35);

        // Plate Number in Large, Gray Background
        doc.setFillColor(200, 200, 200);
        doc.rect(10, 40, 80, 20, 'F');
        doc.setFontSize(24);
        doc.setTextColor(50);
        doc.text(data.plate.split('').join(' '), 15, 55);
        doc.setTextColor(0);

        // ALPR Details
        const locationPoint: Point = {
          type: 'Point',
          coordinates: data.geometry.coordinates,
        };
        const isValidLocation = !this._isInvalidLocation(locationPoint);
        const plateStateValue = data.displayState;
        const makeValue =
          data.vehicleProperties?.make?.name || data['vehicleMake'] || data['vehicle_make'];
        const bodyValue =
          data.vehicleProperties?.body?.name || data['vehicleType'] || data['vehicle_body_type'];
        const colorValue =
          data.vehicleProperties?.color?.name || data['color'] || data['vehicle_color'];
        const yearValue = data['vehicle_year'] || data['vehicleYear'];
        const hitReason = data.hitReason || data['hit_reason'] || data['hitReason'];
        const collectingOfficerValue = data.owner
          ? `${data.owner.firstName || ''} ${data.owner.lastName || ''}`.trim()
          : null;

        doc.setFontSize(14);
        doc.text('ALPR Details', 100, 45);
        doc.setFontSize(12);
        doc.text(`OCR Result: ${data.plate}`, 100, 55);
        doc.text(
          `Plate State: ${this._formatValue(plateStateValue, (val) =>
            this._stateConvertPipe.transform(val, 'long'),
          )}`,
          100,
          63,
        );
        doc.text(
          `Date and Time: ${this._formatDatePipe.transform(
            new Date(data.createdTimestamp),
            'timeFirstNoAmPm',
          )}`,
          100,
          71,
        );

        // Only show GPS coordinates if location is valid
        if (isValidLocation) {
          doc.text(
            `GPS: ${data.geometry.coordinates[1]}, ${data.geometry.coordinates[0]}`,
            100,
            79,
          );
        } else {
          doc.text('GPS: N/A', 100, 79);
        }

        doc.text(`Collecting Officer: ${this._formatValue(collectingOfficerValue)}`, 100, 87);

        // Fix vehicle attributes section
        doc.setFontSize(12);
        doc.text('Vehicle Attributes:', 100, 95);
        doc.text(`Make: ${this._formatValue(makeValue, capitalize)}`, 110, 103);
        doc.text(`Body: ${this._formatValue(bodyValue, capitalize)}`, 110, 111);
        doc.text(
          `Color: ${this._formatValue(colorValue, this._formatColorPipe.transform)}`,
          110,
          119,
        );
        doc.text(`Year: ${this._formatValue(yearValue)}`, 110, 127);
        doc.line(10, 137, 200, 137);

        // Hotlist Information
        doc.setFontSize(14);
        doc.text('Hotlist Information', 10, 147);
        doc.setFontSize(12);
        doc.text(`Hit Reason: ${this._formatValue(hitReason)}`, 10, 155);
        doc.line(10, 165, 200, 165);

        // Vehicle Images
        const loadImage = (
          url: string,
        ): Promise<{ base64: string; width: number; height: number }> => {
          return new Promise((resolve) => {
            this.getBase64FromUrl(url, (base64) => {
              if (base64) {
                const img = new Image();
                img.onload = () => {
                  resolve({ base64, width: img.width, height: img.height });
                };
                img.src = base64;
              } else {
                resolve(null);
              }
            });
          });
        };

        const addImageToPdf = (
          imageData: { base64: string; width: number; height: number },
          x: number,
          y: number,
          label: string,
          width: number,
        ): Promise<number> => {
          return new Promise((resolve) => {
            if (imageData) {
              doc.text(label, x, y);
              const aspectRatio = imageData.width / imageData.height;
              const height = width / aspectRatio;
              doc.addImage(imageData.base64, 'JPEG', x, y + 5, width, height);
              resolve(y + height + 20); // Return next y position if image was added
            } else {
              doc.text(`${label}: N/A`, x, y);
              resolve(y + 10); // Return next y position if no image
            }
          });
        };

        const processImages = () => {
          let currentY = 175;
          const maxY = 250; // Maximum Y position before needing a new page

          // Load both images first to calculate total height needed
          Promise.all([loadImage(data.plateImageUrl), loadImage(data.contextImageUrl)]).then(
            ([plateImage, contextImage]) => {
              // Calculate dimensions for both images to fit side by side
              const totalWidth = 190; // Total available width
              const imageWidth = (totalWidth - 20) / 2; // Width for each image (with spacing)

              // Calculate maximum available height for images
              const availableHeight = maxY - currentY;

              // Function to calculate optimal dimensions while maintaining aspect ratio
              const calculateOptimalDimensions = (image: { width: number; height: number }) => {
                const aspectRatio = image.width / image.height;
                let finalWidth = imageWidth;
                let finalHeight = finalWidth / aspectRatio;

                // If height exceeds available space, scale down proportionally
                if (finalHeight > availableHeight) {
                  finalHeight = availableHeight;
                  finalWidth = finalHeight * aspectRatio;
                }

                return { width: finalWidth, height: finalHeight };
              };

              // Calculate optimal dimensions for both images
              const plateDimensions = plateImage ? calculateOptimalDimensions(plateImage) : null;
              const contextDimensions = contextImage
                ? calculateOptimalDimensions(contextImage)
                : null;

              // Add plate image on the left
              addImageToPdf(
                plateImage,
                10,
                currentY,
                'Plate Patch Image',
                plateDimensions?.width || imageWidth,
              )
                .then((newY) => {
                  // Add context image on the right
                  return addImageToPdf(
                    contextImage,
                    105,
                    currentY,
                    'Context Image',
                    contextDimensions?.width || imageWidth,
                  );
                })
                .then((newY) => {
                  currentY = newY;

                  if (!isValidLocation) {
                    doc.text('Location: N/A', 110, currentY);
                    const pdfBlob = doc.output('blob');
                    resolve(pdfBlob);
                  } else {
                    doc.addPage();
                    doc.text('Map', 15, 20);

                    this.generateMapImage(data.geometry.coordinates, (mapBase64) => {
                      if (mapBase64) {
                        doc.addImage(mapBase64, 'JPEG', 15, 30, 180, 100);
                      }
                      doc.text(
                        `GPS: ${data.geometry.coordinates[1]}, ${data.geometry.coordinates[0]}`,
                        15,
                        140,
                      );

                      const pdfBlob = doc.output('blob');
                      resolve(pdfBlob);
                    });
                  }
                });
            },
          );
        };

        processImages();
      });
    });
  }

  private getBase64FromUrl(url: string, callback: (base64: string) => void): void {
    fetch(url, { mode: 'cors', cache: 'no-cache' })
      .then((response) => response.blob())
      .then((blob) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);

        reader.onloadend = () => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);

            callback(canvas.toDataURL('image/jpeg'));
          };
          img.onerror = () => callback('');
          img.src = reader.result as string;
        };
        reader.onerror = () => callback('');
      })
      .catch(() => callback(''));
  }

  // get the logo base64 without any canvas scaling
  private getLogoBase64(url: string, callback: (base64: string) => void): void {
    fetch(url, { mode: 'cors', cache: 'no-cache' })
      .then((response) => response.blob())
      .then((blob) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => callback(reader.result as string);
        reader.onerror = () => callback('');
      })
      .catch(() => callback(''));
  }

  private generateMapImage(coordinates: number[], callback: (base64: string) => void): void {
    const mapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${coordinates[1]},${coordinates[0]}&zoom=15&size=400x300&markers=color:red%7C${coordinates[1]},${coordinates[0]}&key=${GOOGLE_MAP_KEY}`;
    this.getBase64FromUrl(mapUrl, callback);
  }

  private _isInvalidLocation(point: Point): boolean {
    if (!point || point.coordinates?.length !== 2) {
      return true;
    }
    const coordinates = point.coordinates;
    // Making coordinates a string to account for Vigilant API returning number strings
    return coordinates[0].toString() === '0' && coordinates[1].toString() === '0';
  }

  private _formatValue(
    value: string | null | undefined,
    formatter?: (val: string) => string,
  ): string {
    if (!value || value?.toLowerCase() === 'unknown') {
      return 'N/A';
    }

    return formatter ? formatter(value) : value;
  }
}
